import React, { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import { TextField, InputAdornment, IconButton } from "@mui/material/";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import actions from "../../../Store/actions";
import dayjs from "dayjs";

// Create a mapping of locale codes to their respective dayjs locale codes
const locales = {
    de: () => import("dayjs/locale/de"),
    en: () => import("dayjs/locale/en"), // English (default)
    es: () => import("dayjs/locale/es"), // Spanish
    fr: () => import("dayjs/locale/fr"), // French
    it: () => import("dayjs/locale/it"), // Italian
};

const CustomDatePicker = ({
    datepickerLabel,
    disableDatePicker,
    isFutureDisabled,
}) => {
    const { t, i18n } = useTranslation();
    const [locale, setLocale] = useState(i18n.language); // Default locale
    const dispatch = useDispatch();
    const { exclusionReasonDate } = useSelector((state) => state.assetDetails);

    const [draftDate, setDraftDate] = useState(dayjs()); // Temporary selection for UI
    const [open, setOpen] = useState(false); // Control the open state of DatePicker

    /** Format date to display in the input field */
    const formatDate = (date) =>
        new Date(date).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });

    /* Dynamically set a locale based on the selected language*/
    const loadLocale = (localeCode) => {
        const selectedLocale =
            locales[localeCode]().then(() => dayjs.locale(localeCode)) || "en"; // Fallback to 'en' if localeCode not found
        dayjs.locale(selectedLocale); // Set dayjs locale
        setLocale(selectedLocale); // Update the state
    };

    useEffect(() => {
        loadLocale(i18n.language); // Load selected language
    }, [i18n]);

    /** Handle date change */
    const handleDateChange = (date) => {
        setDraftDate(date);
        // setOpen(false); // Close the DatePicker after date is selected
    };

    const handleAccept = (date) => {
        if (date) {
            // setDate(date); // Only confirm date when user clicks OK
            setDraftDate(date); // Update draft selection when picking
            dispatch(
                actions.assetDetails.create.set({
                    exclusionReasonDate: new Date(date).toISOString(),
                })
            );
        }
        setOpen(false);
    };
    const handleClose = () => {
        setOpen(false);
    };

    /** Handle opening the DatePicker when the icon is clicked */
    const handleIconClick = () => setOpen(true); // Open the DatePicker

    return (
        <>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
            >
                <DatePicker
                    disabled={disableDatePicker}
                    disableFuture={isFutureDisabled}
                    label={datepickerLabel}
                    value={exclusionReasonDate}
                    open={open} // Control the open state manually
                    onOpen={() => setOpen(true)}
                    onClose={handleClose}
                    onChange={handleDateChange}
                    onAccept={handleAccept}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            disabled={disableDatePicker}
                            value={
                                exclusionReasonDate
                                    ? formatDate(exclusionReasonDate)
                                    : ""
                            }
                            onClick={() => setOpen(true)} // Ensure input click opens the DatePicker
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleIconClick}>
                                            <DateRangeIcon color="inherit" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                />
            </LocalizationProvider>
        </>
    );
};

export default CustomDatePicker;
